/**
 * Commodity market — phase 8.3's price model, shared so the client can
 * render quotes it can trust and the server can validate every trade.
 *
 * Three layers:
 *  1. BASE VALUE per good (this file, static).
 *  2. SYSTEM MULTIPLIER from archetype supply/demand — a pure function of
 *     the system seed: volcanic worlds vein volatite, so volatite is CHEAP
 *     there and DEAR in systems with none. This is what makes hauling a
 *     navigation problem (the phase-7 design note, now with a payoff).
 *  3. DRIFT — a server-owned random walk per system+good so prices
 *     breathe over time. Server state, never derived client-side.
 *
 * Stations quote a SPREAD around the local price: they sell to players at
 * a markup and buy at a discount — the house margin is the credit sink.
 */
import type { SystemDef } from "./universe.js";

/** Goods stations trade, with their galactic base value (credits/unit). */
export const MARKET_GOODS: Record<string, number> = {
  "ore-volatite": 8,
  "ore-ferrite": 6,
  "ore-cryonite": 9,
  "scrap-alloy": 14,
  "exotic-crystal": 65,
  "fuel-cell": 32,
  medkit: 26,
  "shield-pack": 48,
  "ammo-light": 4,
  "ammo-heavy": 9,
};

/** Station sells to players at price × SELL_MARKUP. */
export const SELL_MARKUP = 1.15;
/** Station buys from players at price × BUY_DISCOUNT. */
export const BUY_DISCOUNT = 0.85;
/** Drift random walk bounds (multiplier on the local price). */
export const DRIFT_MIN = 0.7;
export const DRIFT_MAX = 1.4;
/** Largest per-trade quantity the server will accept. */
export const TRADE_MAX = 99;

/** Archetype → which ore it makes LOCALLY ABUNDANT (mirrors kindWeights). */
const SUPPLY: Record<string, string> = {
  volcanic: "ore-volatite",
  desert: "ore-volatite",
  ice: "ore-cryonite",
  barren: "ore-ferrite",
};

/**
 * Per-system price multipliers from planetary supply/demand — pure seed.
 * Each supplying world cuts its ore's local price; total absence raises
 * it. Mult clamps to [0.55, 1.6] so nothing degenerates.
 */
export function marketBase(def: SystemDef): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const p of def.planets) {
    const ore = SUPPLY[p.archetype];
    if (ore) counts[ore] = (counts[ore] ?? 0) + 1;
  }
  const out: Record<string, number> = {};
  for (const id of Object.keys(MARKET_GOODS)) {
    let mult = 1;
    if (id.startsWith("ore-")) {
      const n = counts[id] ?? 0;
      mult = n === 0 ? 1.45 : Math.max(0.55, 1 - 0.16 * n);
    } else if (id === "exotic-crystal") {
      // Crystals follow nothing local — slight scarcity everywhere.
      mult = 1.1;
    }
    out[id] = Math.round(MARKET_GOODS[id] * Math.max(0.55, Math.min(1.6, mult)) * 100) / 100;
  }
  return out;
}

/** A station quote for one good. */
export interface MarketQuote {
  /** Station SELLS to the player at this (credits/unit). */
  sell: number;
  /** Station BUYS from the player at this (credits/unit). */
  buy: number;
}

/** Apply drift + spread to a local base price. */
export function quoteFor(basePrice: number, drift: number): MarketQuote {
  const p = basePrice * drift;
  return {
    sell: Math.max(1, Math.round(p * SELL_MARKUP)),
    buy: Math.max(1, Math.round(p * BUY_DISCOUNT)),
  };
}
